import {useRef, useState} from "react";
import {Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography} from "@material-ui/core";
import Add from "@material-ui/icons/Add";
import {
  Button, FormWithRedirect, Record, RecordContextProvider, required,
  useDataProvider, useNotify, useRecordContext, useRefresh,
} from "react-admin";
import {NumberInput} from "../../components/NumberInput";
import {ReferenceInputChartAccounts} from "../../components/reference-inputs/ReferenceInputChartAccounts";
import {ReferenceInputSite} from "../../components/reference-inputs/ReferenceInputSite";
import {generateError} from "../../shared/utils";
import {TariffType} from "../../shared/types";
import {getPensionFundAssignment} from "./pensionFundAssignment";

export const PensionFundChartAccountsAssignment = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [amount, setAmount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);

  if (record.invoice_type !== TariffType.PURCHASE || !Number(record.pension_fund_amount)) {
    return null;
  }

  const unavailable = () => {
    notify("Il residuo da assegnare non è sufficiente per l'intero importo della cassa previdenziale. Verifica le righe già associate.", "warning");
    setAmount(null);
    refresh();
  };

  const openForm = async () => {
    setBusy(true);
    try {
      const assignment = await getPensionFundAssignment(dataProvider, record.id);
      if (assignment.canAssign) {
        setAmount(assignment.amount);
      } else {
        unavailable();
      }
    } catch (error) {
      generateError(error, notify);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (values: Partial<Record>) => {
    // FormWithRedirect retains its initial save callback. Read the submission
    // lock from a ref and the displayed amount from the submitted form values.
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    try {
      const assignment = await getPensionFundAssignment(dataProvider, record.id);
      if (!assignment.canAssign) {
        unavailable();
        return;
      }
      if (Math.round(assignment.amount * 100) !== Math.round(Number(values.amount) * 100)) {
        notify("L'importo della cassa previdenziale è cambiato. Riapri il modulo per aggiornarlo.", "warning");
        setAmount(null);
        refresh();
        return;
      }
      await dataProvider.create("invoicechartaccounts", {data: {
        invoice: record.id,
        chart_accounts: values.chart_accounts,
        site: values.site || null,
        amount: assignment.amount,
      }});
      notify("ra.notification.created", "success");
      setAmount(null);
      refresh();
    } catch (error) {
      generateError(error, notify);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <Box p={2}>
      <Typography variant="subtitle1">
        Cassa previdenziale: {Number(record.pension_fund_amount).toLocaleString("it-IT", {
          style: "currency", currency: "EUR",
        })}
      </Typography>
      <Button label="Assegna cassa al piano dei conti" onClick={openForm} disabled={busy}>
        <Add />
      </Button>
      <Dialog open={amount !== null} onClose={() => { if (!busy) setAmount(null); }} fullWidth maxWidth="md">
        <DialogTitle>Assegna cassa previdenziale al piano dei conti</DialogTitle>
        <RecordContextProvider value={undefined}>
          <FormWithRedirect
            save={onSubmit}
            initialValues={{invoice: record.id, amount}}
            render={formProps => (
              <>
                <DialogContent>
                  <Typography gutterBottom>
                    Scegli il conto e l'eventuale sede a cui associare la cassa previdenziale.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <ReferenceInputChartAccounts source="chart_accounts" label="Piano dei conti"
                        fullWidth filter={{last_level: true}} validate={[required()]} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ReferenceInputSite source="site" label="Sede" fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <NumberInput source="amount" label="Importo cassa previdenziale" fullWidth disabled />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button label="Annulla" onClick={() => setAmount(null)} disabled={busy} />
                  <Button label="Aggiungi" variant="contained" color="primary"
                    onClick={formProps.handleSubmit} disabled={busy || formProps.submitting}>
                    <Add />
                  </Button>
                </DialogActions>
              </>
            )}
          />
        </RecordContextProvider>
      </Dialog>
    </Box>
  );
};
